import { confirmAlert } from 'react-confirm-alert';
import moment from 'moment';

interface MonthDetail {
    month: string;
    current_count: number;
    threshold_worksheet_count: number;
    exceeded_by: number;
    threshold_source: string;
    worksheets_to_print: number;
    total_after_print: number;
}

interface ExceededSubject {
    subject_name: string;
    worksheets_to_print: number;
    threshold_exceeded: boolean;
    month_type?: 'single' | 'multiple';
    details?: {
        current_count: number;
        threshold_worksheet_count: number;
        exceeded_by: number;
        threshold_source?: string;
    };
    month_details?: MonthDetail[];
}

interface ThresholdExceededModalProps {
    exceededSubjects: ExceededSubject[];
    onConfirm: () => void;
    onCancel?: () => void;
}

export const showThresholdExceededModal = ({ exceededSubjects, onConfirm, onCancel }: ThresholdExceededModalProps) => {
    confirmAlert({
        customUI: ({ onClose }) => (
            <div className="confirm-modal">
                <div className="confirm-modal__header" style={{ marginBottom: '15px' }}>
                    <h2>⚠️ Print Threshold Exceeded</h2>
                </div>
                <div className="confirm-modal__content">
                    {exceededSubjects.map((subject, index) => (
                        <div key={index} style={{ marginBottom: index < exceededSubjects.length - 1 ? '15px' : '0' }}>
                            <div style={{ marginBottom: '8px' }}>
                                <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '5px' }}>
                                    Subject: {subject.subject_name}
                                </div>
                                <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                                    Requesting to Print: <b>{subject.worksheets_to_print} worksheets</b>
                                </div>
                            </div>
                            
                            {subject.month_type === 'multiple' && subject.month_details ? (
                                // Show month-wise breakdown for multiple months
                                <div style={{ marginLeft: '10px' }}>
                                    {subject.month_details.filter(m => m.exceeded_by > 0).map((monthDetail, mIndex) => (
                                        <div key={mIndex} style={{ 
                                            marginBottom: '8px', 
                                            padding: '8px', 
                                            backgroundColor: '#fff3cd',
                                            borderLeft: '3px solid #ffc107',
                                            borderRadius: '4px'
                                        }}>
                                            <div style={{ fontWeight: 'bold', marginBottom: '5px', color: '#856404', fontSize: '14px' }}>
                                                {moment(monthDetail.month, 'YYYY-MM').format('MMMM YYYY')}
                                            </div>
                                            <div className="confirm-grid" style={{ marginBottom: '10px', fontSize: '12px' }}>
                                                <div>Requesting to Print:</div>
                                                <div><b>{monthDetail.worksheets_to_print} worksheets</b></div>
                                                
                                                {/* <div>Already Printed:</div>
                                                <div><b>{monthDetail.current_count} worksheets</b></div> */}
                                                
                                                <div>Monthly Threshold:</div>
                                                <div><b>{monthDetail.threshold_worksheet_count} worksheets</b></div>
                                                
                                                <div>Exceeded By:</div>
                                                <div><b style={{ color: '#dc3545' }}>{monthDetail.exceeded_by} worksheets</b></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                // Show single summary for single month or when details available
                                <div className="confirm-grid" style={{ marginBottom: '8px', fontSize: '13px' }}>
                                    {/* <div>Already Printed:</div>
                                    <div><b>{subject.details?.current_count || 0} worksheets</b></div> */}
                                    
                                    <div>Monthly Threshold:</div>
                                    <div><b>{subject.details?.threshold_worksheet_count} worksheets</b></div>
                                    
                                    <div>Exceeded By:</div>
                                    <div><b style={{ color: '#dc3545' }}>{subject.details?.exceeded_by || 0} worksheets</b></div>
                                </div>
                            )}
                            
                            {index < exceededSubjects.length - 1 && (
                                <div style={{ borderTop: '1px solid #e0e0e0', marginTop: '8px' }}></div>
                            )}
                        </div>
                    ))}
                    
                    <div style={{ borderTop: '2px solid #e0e0e0', paddingTop: '12px', marginTop: '15px' }}>
                        <p style={{ fontSize: '13px', lineHeight: '1.5', marginBottom: '10px' }}>
                            {exceededSubjects.length === 1 ? (
                                exceededSubjects[0].month_type === 'multiple' ? (
                                    exceededSubjects[0].month_details && exceededSubjects[0].month_details.length > 0 ? (
                                        <>
                                            The print request for <b>{exceededSubjects[0].subject_name}</b> exceeds the monthly threshold 
                                            for one or more months. Please review the month-wise details above.
                                        </>
                                    ) : (
                                        <>
                                            The print request for <b>{exceededSubjects[0].subject_name}</b> spans multiple months and exceeds 
                                            the monthly threshold. Total requesting to print: <b>{exceededSubjects[0].worksheets_to_print} worksheets</b>.
                                        </>
                                    )
                                ) : exceededSubjects[0].details?.current_count === 0 ? (
                                    <>
                                        No worksheets have been printed for this student in <b>{exceededSubjects[0].subject_name}</b> this month. 
                                        However, printing <b>{exceededSubjects[0].worksheets_to_print}</b> worksheets will exceed the monthly threshold 
                                        of <b>{exceededSubjects[0].details?.threshold_worksheet_count}</b> worksheets.
                                    </>
                                ) : (
                                    <>
                                        <b>{exceededSubjects[0].details?.current_count}</b> worksheets have already been printed for this student in <b>{exceededSubjects[0].subject_name}</b> this 
                                        month. Printing <b>{exceededSubjects[0].worksheets_to_print}</b> more will exceed the monthly threshold of <b>{exceededSubjects[0].details?.threshold_worksheet_count}</b> worksheets.
                                    </>
                                )
                            ) : (
                                <>
                                    The print request exceeds the monthly threshold for <b>{exceededSubjects.length}</b> subjects. 
                                    Please review the details above.
                                </>
                            )}
                        </p>
                        
                        <p style={{ fontSize: '13px', fontWeight: '500', marginTop: '10px' }}>
                            Do you want to continue with this print request?
                        </p>
                    </div>
                </div>
                <div className="confirm-modal__actions">
                    <button
                        className="btn btn-primary"
                        style={{ 
                            padding: '8px 20px',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}
                        onClick={() => {
                            onConfirm();
                            onClose();
                        }}
                    >
                        Continue
                    </button>
                    <button
                        className="btn btn-secondary"
                        style={{ 
                            padding: '8px 20px',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}
                        onClick={() => {
                            if (onCancel) onCancel();
                            onClose();
                        }}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        )
    });
};
